import { useLocation, useRouteLoaderData } from '@remix-run/react';
import { useMemo } from 'react';

import { useCustomerSafe } from '~/context/CustomerContext';
import { useVisitorCookie } from '~/gtm/hooks/useVisitorCookie';
import type { PageDimensions } from '~/gtm/types';
import { getGAClientId } from '~/gtm/utils/clientId';
import type { loader as rootLoader } from '~/root';

/**
 * Determine page type from pathname
 * Values per GTM spec: HP, category, product, searchresults, cart, checkout, thankyou, login, my-account, content
 */
function getPageType(pathname: string): string {
  // Remove locale prefix if present (e.g., /en/, /cs/)
  const pathWithoutLocale = pathname.replace(/^\/(cs|en)/, '') || '/';

  if (pathWithoutLocale === '/' || pathWithoutLocale === '') {
    return 'HP';
  }
  if (pathWithoutLocale.startsWith('/product/') || pathWithoutLocale.startsWith('/produkt/')) {
    return 'product';
  }
  if (pathWithoutLocale.startsWith('/category/') || pathWithoutLocale.startsWith('/kategorie/')) {
    return 'category';
  }
  if (pathWithoutLocale.startsWith('/search') || pathWithoutLocale.startsWith('/vyhledavani')) {
    return 'searchresults';
  }
  if (pathWithoutLocale.startsWith('/cart') || pathWithoutLocale.startsWith('/kosik')) {
    return 'cart';
  }
  if (pathWithoutLocale.startsWith('/checkout') || pathWithoutLocale.startsWith('/pokladna')) {
    return 'checkout';
  }
  if (pathWithoutLocale.startsWith('/thank-you') || pathWithoutLocale.startsWith('/dekujeme')) {
    return 'thankyou';
  }
  if (pathWithoutLocale.startsWith('/account') || pathWithoutLocale.startsWith('/ucet')) {
    return 'my-account';
  }
  if (pathWithoutLocale.startsWith('/login') || pathWithoutLocale.startsWith('/prihlaseni')) {
    return 'login';
  }
  if (pathWithoutLocale.startsWith('/register') || pathWithoutLocale.startsWith('/registrace')) {
    return 'login';
  }

  return 'content';
}

/**
 * Extract user ID from customer @id
 * Format: /api/v2/shop/customers/123 -> "123"
 */
function extractUserId(customerId?: string): string | undefined {
  if (!customerId) return undefined;
  const parts = customerId.split('/');
  return parts[parts.length - 1];
}

/**
 * Hook to compute page-level dimensions for GTM
 */
export function usePageDimensions(): PageDimensions {
  const location = useLocation();
  const rootData = useRouteLoaderData<typeof rootLoader>('root');
  const customerContext = useCustomerSafe();
  const customer = customerContext?.customer ?? null;
  const customerLoading = customerContext?.loading ?? false;
  const visitorType = useVisitorCookie();

  return useMemo(() => {
    const isLoggedIn = !!customer && !customerLoading;
    const hasEverRegistered = !!customer?.user?.verified;

    const visitorLoginState: 'user' | 'visitor' = isLoggedIn ? 'user' : 'visitor';

    let visitorState: 'loggedin' | 'registered' | 'notregistered';
    if (isLoggedIn) {
      visitorState = 'loggedin';
    } else if (hasEverRegistered) {
      visitorState = 'registered';
    } else {
      visitorState = 'notregistered';
    }

    const dimensions: PageDimensions = {
      visitorType,
      visitorLoginState,
      visitorState,
      pageType: getPageType(location.pathname),
      language: rootData?.locale || 'cs',
      currency: rootData?.currency || 'CZK',
    };

    // Add user ID if logged in
    if (isLoggedIn && customer?.['@id']) {
      dimensions.userID = extractUserId(customer['@id']);
    }

    // Add GA client ID if available
    if (typeof window !== 'undefined') {
      const clientId = getGAClientId();
      if (clientId) {
        dimensions.clientID = clientId;
      }
    }

    // Build breadcrumbs path from pathname
    const pathSegments = location.pathname.split('/').filter((segment) => segment && !['cs', 'en'].includes(segment));

    if (pathSegments.length > 0) {
      dimensions.breadcrumbsPath = pathSegments.join('/');
    }

    return dimensions;
  }, [location.pathname, rootData?.locale, rootData?.currency, customer, customerLoading, visitorType]);
}
